<script setup>
import { useAnalysisStore } from '@/store/modules/analysis';
import { addContentLogAPI } from '@/api/logServiceApi';
import { computed } from 'vue';
import IconAnalysNav220 from '@/components/svg/IconAnalysNav220.vue';
import { calculateDDay } from '@/utils/dateFormatter';

const analysisStore = useAnalysisStore();

const props = defineProps({
  guestUserId: {
    required: true,
  },
  yongsinLightTicketData: {
    required: true,
  },
  yongsinLightLogData: {
    required: true,
  },
  hasPermission: {
    required: false,
  },
});

const emit = defineEmits(['goPopAnalysis']);

const logData = computed(() => {
  if (!props.yongsinLightLogData?.length) return null;
  return props.yongsinLightLogData[0];
});

const dDay = computed(() => calculateDDay(logData.value?.expiryDate, false));

const formatDate = (dateString) => {
  if (!dateString) return '';
  const date = new Date(dateString);
  return date.toISOString().split('T')[0];
};

const formattedCreatedAt = computed(() => formatDate(logData.value?.createdAt));
const formattedExpiryDate = computed(() => formatDate(logData.value?.expiryDate));

const isDetailPage = computed(() => analysisStore.selectedMenu?.startsWith('/ys-light'));

const goPopAnalysis = () => {
  emit('goPopAnalysis');
};

const clickGoYongsinLightButton = () => {
  addContentLogAPI({
    contentType: props.hasPermission ? 'ay_yongsin_light_btn_guide_paid' : 'ay_yongsin_light_btn_guide_free',
  });
  if (isDetailPage.value && !props.hasPermission) {
    emit('goPopAnalysis');
    return;
  }
  analysisStore.navigateToContent('/ys-light', 0);
};
</script>
<template>
  <div class="overview-payment-info-wrap st-yongsin-light">
    <div class="overview-payment-inner">
      <div class="overview-none-wrap" v-if="!hasPermission">
        <div class="overview-none-title">
          <i class="ticket-icon__analysis"></i>
          <span class="tit">보유 용신분석권</span>
          <span class="cnt">{{ yongsinLightTicketData?.totalCount || 0 }} 건</span>
        </div>
      </div>

      <!-- 분석권으로 이용 중일 때 -->
      <div class="overview-payment-info-content-box" v-else>
        <div class="overview-payment-info-content">
          <div class="overview-payment-info-title">용신라이트를 이용하고 있어요!</div>
          <div class="overview-payment-info-date-box">
            <div class="overview-payment-info-date">
              <div class="overview-payment-info-date-title">시작일</div>
              <div class="overview-payment-info-date-value">{{ formattedCreatedAt || '-' }}</div>
            </div>
            <div class="overview-payment-info-date">
              <div class="overview-payment-info-date-title">만료일</div>
              <div class="overview-payment-info-date-value" v-if="logData?.expiryDate">
                {{ formattedExpiryDate }}
              </div>
              <div class="overview-payment-info-date-value" v-else>-</div>
            </div>
            <div class="overview-payment-info-date">
              <div class="overview-payment-info-date-title">남은 기간</div>
              <div class="overview-payment-info-date-value red" v-if="logData?.expiryDate">
                {{ dDay }} 일 남음
              </div>
              <div class="overview-payment-info-date-value red" v-else>소장용</div>
            </div>
          </div>
        </div>
      </div>

      <!-- 용신라이트 소개 -->
      <div class="overview-pay-info-box">
        <div class="tit-box"><IconAnalysNav220 :currentColor="'#a6c7ff'" /> <span>나를 돕는 기운</span></div>
        <div class="txt-box">
          <div class="txt">
            앗, 나만 몰랐나요?<br />
            나를 힘나게 하는 오행이 있어요.
          </div>
          <div class="txt">
            괜히 잘 풀리는 날, 이상하게 힘이 나는 순간,<br />
            그냥 기분 탓만은 아니에요.
          </div>
          <div class="txt">
            그 안에는 보이지 않지만 느껴지는<br />
            나에게 맞는 에너지가 있습니다.
          </div>
          <div class="txt">
            사주를 깊이 있게 풀어<br />
            <span class="txt-bold">나에게 꼭 맞고 필요한 에너지를 찾아주는,</span><br />
            <span class="txt-bold">낭만만세력의 용신 서비스</span>입니다.
          </div>
        </div>
      </div>
    </div>

    <div class="overview_guide_button_box">
      <div
        :class="['overview_guide_button_start', { 'is-solid': isDetailPage && !props.hasPermission }]"
        @click="clickGoYongsinLightButton"
      >
        나를 돕는 기운 시작하기
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.overview-payment-info-wrap {
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  align-items: center;
  flex-shrink: 0;
  height: 100%;
  box-sizing: border-box;
  width: 100%;
  padding: 28px;
  gap: 24px;
  overflow-y: scroll;
}

.st-yongsin-light {
  .overview-none-wrap {
    width: 100%;
    display: flex;
    flex-direction: column;
    .overview-none-title {
      width: 100%;
      display: flex;
      justify-content: flex-start;
      align-items: center;
      padding: 20px 28px;
      border-radius: 12px;
      border: 1px solid var(--Gray-300, #ebebeb);
      background: var(--gray-100-white, #fff);
      .tit {
        color: var(--Gray-800, #333);
        font-size: 15px;
        font-style: normal;
        font-weight: 700;
        line-height: 24px;
      }
      .cnt {
        color: var(--orange-300-main, #eb4c10);
        font-size: 14px;
        font-style: normal;
        font-weight: 700;
        line-height: 24px;
        margin-left: auto;
      }
    }
  }

  .overview-pay-info-box {
    display: flex;
    padding: 40px;
    flex-direction: column;
    align-items: flex-start;
    gap: 20px;
    border-radius: 12px;
    background: rgba(78, 128, 214, 0.05);
    box-sizing: border-box;
    width: 100%;
    .tit-box {
      color: #4e80d6;
      font-family: Pretendard;
      font-size: 18px;
      font-style: normal;
      font-weight: 700;
      line-height: 28px;

      display: flex;
      align-items: center;
      gap: 8px;

      span {
        display: inline-flex;
        align-items: center;
        line-height: inherit;
      }
    }
    .txt-box {
      display: flex;
      flex-direction: column;
      align-items: flex-start;
      gap: 8px;
      color: var(--Gray-700, #575757);
      font-size: 15px;
      font-style: normal;
      font-weight: 500;
      line-height: 24px;

      .txt-bold {
        color: #5d81bf;
        font-weight: 700;
      }
    }
  }

  .overview-payment-info-content-box {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    width: 100%;
    gap: 20px;
  }
  .overview-payment-info-content {
    border-radius: 12px;
    border: 1px solid var(--Gray-300, #ebebeb);
    background: var(--gray-100-white, #fff);
    width: 100%;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    box-sizing: border-box;
    padding: 20px 28px;

    .overview-payment-info-title {
      width: 100%;
      display: flex;
      justify-content: flex-start;
      align-items: center;
      color: var(--Gray-800, #333);
      font-size: 15px;
      font-style: normal;
      font-weight: 700;
      line-height: 24px;
      margin-bottom: 8px;
    }

    .overview-payment-info-date-box {
      display: flex;
      width: 100%;
      justify-content: flex-start;
      align-items: flex-start;
      flex-direction: column;
    }
    .overview-payment-info-date {
      display: flex;
      width: 100%;
      justify-content: space-between;
      align-items: flex-start;
      .overview-payment-info-date-title {
        color: var(--Gray-800, #333);
        font-size: 14px;
        font-style: normal;
        font-weight: 400;
        line-height: 24px;
        display: flex;
        align-items: center;
        gap: 4px;
      }
      .overview-payment-info-date-value {
        color: var(--Gray-800, #333);
        font-size: 14px;
        font-style: normal;
        font-weight: 700;
        line-height: 24px;
        &.red {
          color: var(--orange-300-main, #eb4c10);
        }
      }
    }
  }
  .overview_guide_button_box {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    width: 100%;
    gap: 28px;
    margin-top: auto;
  }
  .overview_guide_button {
    display: flex;
    padding: 14px 20px;
    justify-content: center;
    align-items: center;
    gap: 4px;
    align-self: stretch;
    border-radius: 6px;
    border: 1px solid var(--orange-300-main, #eb4c10);
    background: var(--gray-100-white, #fff);
    color: var(--orange-300-main, #eb4c10);
    text-align: center;
    font-family: Pretendard;
    font-size: 15px;
    font-style: normal;
    font-weight: 700;
    line-height: 20px;
    cursor: pointer;
  }
  .overview_guide_button_start {
    cursor: pointer;
    display: flex;
    width: 100%;
    padding: 14px 20px;
    justify-content: center;
    align-items: center;
    gap: 4px;
    border-radius: 6px;
    border: 1px solid var(--orange-300-main, #eb4c10);
    background: var(--gray-100-white, #fff);
    color: var(--orange-300-main, #eb4c10);
    text-align: center;
    font-size: 15px;
    font-style: normal;
    font-weight: 700;
    line-height: 20px;

    &.is-solid {
      background: var(--orange-300-main, #eb4c10);
      color: #fff;
    }
  }
  .ticket-icon__analysis {
    background: url('@manseImages/ticket-icon.svg') center center no-repeat;
    background-size: contain;
    width: 20px;
    height: 20px;
    display: inline-block;
    vertical-align: middle;
    margin-right: 8px;
  }

  .overview-payment-inner {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 20px;
    align-self: stretch;
  }
}
</style>
